<script lang="ts">
  import { Button } from "$lib/components/ui/button";
  import { Input, DecimalInput } from "$lib/components/ui/input";
  import { Switch } from "$lib/components/ui/switch";
  import * as Select from "$lib/components/ui/select/index.js";
  import { t, locale, type TranslationKey } from "$lib/i18n";
  import {
    type SecurityPerforation,
    type SecurityHoleShape,
    type SecurityHoleScope,
    type SecurityHolePosition,
    type SecurityHoleOrientation,
    securityHoleShapes,
    securityHoleScopes,
    securityHolePositions,
    securityHoleOrientations,
    securityHoleUsesOrientation,
    securityHoleUsesPoints,
    emptyDimensionsForShape,
    defaultSecurityPerforation,
    withSecurityHoleShape,
  } from "@briefmarken_katalog/types";
  import { formatSecurityPerforation, securityHoleShapeLabel } from "$lib/utils/securityPerforation";

  let {
    security = $bindable<SecurityPerforation | undefined>(undefined),
    isReadonly = false,
  }: { security: SecurityPerforation | undefined; isReadonly?: boolean } = $props();

  let isOpen = $state(false);

  const summary = $derived(security ? formatSecurityPerforation(security, $locale) : "");

  function patch(changes: Partial<SecurityPerforation>) {
    if (!security) return;
    security = { ...security, ...changes };
  }

  function toggleSecurity(on: boolean) {
    security = on ? defaultSecurityPerforation() : undefined;
  }

  function setShape(shape: SecurityHoleShape) {
    if (!security) return;
    security = withSecurityHoleShape(security, shape);
  }

  /** "Mittig" carries no index, so the stale number must not linger in the stored value. */
  function setPosition(position: SecurityHolePosition) {
    if (!security) return;
    if (position === "Center") {
      const { positionIndex, ...rest } = security;
      security = { ...rest, position };
    } else {
      security = { ...security, position, positionIndex: security.positionIndex ?? 1 };
    }
  }

  /** The measurement is optional as a whole — an unknown one stays absent, not zero. */
  function toggleDimensions(on: boolean) {
    if (!security) return;
    if (on) {
      security = { ...security, dimensions: emptyDimensionsForShape(security.shape) };
    } else {
      const { dimensions, ...rest } = security;
      security = rest;
    }
  }

  function patchDimensions(changes: Record<string, number | undefined>) {
    if (!security?.dimensions) return;
    security = { ...security, dimensions: { ...security.dimensions, ...changes } as typeof security.dimensions };
  }
</script>

{#if isReadonly}
  {#if security}
    <div class="grid grid-cols-4 items-start gap-4">
      <span class="text-right text-sm font-medium pt-1">{$t('fields.securityPerforation.label')}:</span>
      <span class="col-span-3 text-sm">{summary}</span>
    </div>
  {/if}
{:else}
  <div class="relative">
    <Button variant="outline" type="button" onclick={() => (isOpen = !isOpen)}>
      {$t('fields.securityPerforation.button')}{security ? ` (${securityHoleShapeLabel(security)})` : ''}
    </Button>

    {#if isOpen}
      <div class="fixed inset-0 z-10" role="presentation" onclick={() => (isOpen = false)}></div>
      <div class="absolute left-0 bottom-full z-20 mb-1 w-[26rem] rounded-md border bg-background shadow-md p-3 flex flex-col gap-3">

        <label class="flex items-center gap-2 text-sm font-medium">
          <Switch checked={!!security} onCheckedChange={toggleSecurity} />
          {$t('fields.securityPerforation.enabled')}
        </label>

        {#if security}
          <div class="flex items-center gap-2 text-sm">
            <span class="w-40 shrink-0">{$t('fields.securityPerforation.shape')}</span>
            <Select.Root type="single" value={security.shape} onValueChange={(v) => setShape(v as SecurityHoleShape)}>
              <Select.Trigger class="flex-1 h-7 text-sm">
                {$t(`fields.securityPerforation.shapes.${security.shape}` as TranslationKey)}
              </Select.Trigger>
              <Select.Content>
                {#each securityHoleShapes as shape}
                  <Select.Item value={shape} label={$t(`fields.securityPerforation.shapes.${shape}` as TranslationKey)}>
                    {$t(`fields.securityPerforation.shapes.${shape}` as TranslationKey)}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </div>

          {#if security.shape === 'Motif'}
            <div class="flex items-center gap-2 text-sm">
              <span class="w-40 shrink-0">{$t('fields.securityPerforation.shapeText')}</span>
              <Input
                class="flex-1 h-7 text-sm"
                placeholder={$t('fields.securityPerforation.shapeTextPlaceholder')}
                value={security.shapeText ?? ''}
                oninput={(e) => patch({ shapeText: (e.target as HTMLInputElement).value })}
              />
            </div>
          {/if}

          <div class="flex items-center gap-2 text-sm">
            <span class="w-40 shrink-0">{$t('fields.securityPerforation.holesPerSide')}</span>
            <Input
              type="number"
              min="1"
              step="1"
              class="w-24 h-7 text-sm"
              value={security.holesPerSide}
              oninput={(e) => patch({ holesPerSide: Number((e.target as HTMLInputElement).value) })}
            />
          </div>

          <div class="flex items-center gap-2 text-sm">
            <span class="w-40 shrink-0">{$t('fields.securityPerforation.scope')}</span>
            <Select.Root type="single" value={security.scope} onValueChange={(v) => patch({ scope: v as SecurityHoleScope })}>
              <Select.Trigger class="flex-1 h-7 text-sm">
                {$t(`fields.securityPerforation.scopes.${security.scope}` as TranslationKey)}
              </Select.Trigger>
              <Select.Content>
                {#each securityHoleScopes as scope}
                  <Select.Item value={scope} label={$t(`fields.securityPerforation.scopes.${scope}` as TranslationKey)}>
                    {$t(`fields.securityPerforation.scopes.${scope}` as TranslationKey)}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
          </div>

          <label class="flex items-center gap-2 text-sm">
            <span class="w-40 shrink-0">{$t('fields.securityPerforation.innerSeparationOnly')}</span>
            <Switch
              checked={!!security.innerSeparationOnly}
              onCheckedChange={(on) => patch({ innerSeparationOnly: on || undefined })}
            />
          </label>

          <div class="flex items-center gap-2 text-sm">
            <span class="w-40 shrink-0">{$t('fields.securityPerforation.position')}</span>
            <Select.Root type="single" value={security.position} onValueChange={(v) => setPosition(v as SecurityHolePosition)}>
              <Select.Trigger class="flex-1 h-7 text-sm">
                {$t(`fields.securityPerforation.positionLabels.${security.position}` as TranslationKey)}
              </Select.Trigger>
              <Select.Content>
                {#each securityHolePositions as position}
                  <Select.Item value={position} label={$t(`fields.securityPerforation.positionLabels.${position}` as TranslationKey)}>
                    {$t(`fields.securityPerforation.positionLabels.${position}` as TranslationKey)}
                  </Select.Item>
                {/each}
              </Select.Content>
            </Select.Root>
            {#if security.position === 'Nth'}
              <Input
                type="number"
                min="1"
                step="1"
                class="w-20 h-7 text-sm"
                aria-label={$t('fields.securityPerforation.positionIndex')}
                value={security.positionIndex ?? 1}
                oninput={(e) => patch({ positionIndex: Number((e.target as HTMLInputElement).value) })}
              />
            {/if}
          </div>

          {#if securityHoleUsesOrientation(security.shape)}
            <div class="flex items-center gap-2 text-sm">
              <span class="w-40 shrink-0">{$t('fields.securityPerforation.orientation')}</span>
              <Select.Root
                type="single"
                value={security.orientation ?? 'ParallelToRow'}
                onValueChange={(v) => patch({ orientation: v as SecurityHoleOrientation })}
              >
                <Select.Trigger class="flex-1 h-7 text-sm">
                  {$t(`fields.securityPerforation.orientations.${security.orientation ?? 'ParallelToRow'}` as TranslationKey)}
                </Select.Trigger>
                <Select.Content>
                  {#each securityHoleOrientations as orientation}
                    <Select.Item value={orientation} label={$t(`fields.securityPerforation.orientations.${orientation}` as TranslationKey)}>
                      {$t(`fields.securityPerforation.orientations.${orientation}` as TranslationKey)}
                    </Select.Item>
                  {/each}
                </Select.Content>
              </Select.Root>
            </div>
          {/if}

          <label class="flex items-center gap-2 text-sm font-medium border-t pt-2">
            <Switch checked={!!security.dimensions} onCheckedChange={toggleDimensions} />
            {$t('fields.securityPerforation.dimensions')}
          </label>

          {#if security.dimensions?.kind === 'Round'}
            {@const dims = security.dimensions}
            <div class="flex items-center gap-2 text-sm">
              <span class="w-40 shrink-0">{$t('fields.securityPerforation.diameter')}</span>
              <DecimalInput
                class="w-24 h-7 text-sm"
                bind:value={() => dims.diameter, (v) => patchDimensions({ diameter: v })}
              />
              <span class="text-muted-foreground">{$t('fields.mm')}</span>
            </div>
          {:else if security.dimensions?.kind === 'Elliptic'}
            {@const dims = security.dimensions}
            <div class="flex items-center gap-2 text-sm">
              <span class="w-40 shrink-0">{$t('fields.securityPerforation.majorAxis')}</span>
              <DecimalInput
                class="w-24 h-7 text-sm"
                bind:value={() => dims.majorAxis, (v) => patchDimensions({ majorAxis: v })}
              />
              <span class="text-muted-foreground">{$t('fields.mm')}</span>
            </div>
            <div class="flex items-center gap-2 text-sm">
              <span class="w-40 shrink-0">{$t('fields.securityPerforation.minorAxis')}</span>
              <DecimalInput
                class="w-24 h-7 text-sm"
                bind:value={() => dims.minorAxis, (v) => patchDimensions({ minorAxis: v })}
              />
              <span class="text-muted-foreground">{$t('fields.mm')}</span>
            </div>
          {:else if security.dimensions?.kind === 'Polygonal'}
            {@const dims = security.dimensions}
            <div class="flex items-center gap-2 text-sm">
              <span class="w-40 shrink-0">{$t('fields.securityPerforation.circumradius')}</span>
              <DecimalInput
                class="w-24 h-7 text-sm"
                bind:value={() => dims.circumradius, (v) => patchDimensions({ circumradius: v })}
              />
              <span class="text-muted-foreground">{$t('fields.mm')}</span>
            </div>
            <div class="flex items-center gap-2 text-sm">
              <span class="w-40 shrink-0">{$t('fields.securityPerforation.inradius')}</span>
              <DecimalInput
                class="w-24 h-7 text-sm"
                bind:value={() => dims.inradius, (v) => patchDimensions({ inradius: v })}
              />
              <span class="text-muted-foreground">{$t('fields.mm')}</span>
            </div>
            {#if securityHoleUsesPoints(security.shape)}
              <div class="flex items-center gap-2 text-sm">
                <span class="w-40 shrink-0">{$t('fields.securityPerforation.points')}</span>
                <Input
                  type="number"
                  min="3"
                  step="1"
                  class="w-20 h-7 text-sm"
                  value={dims.points ?? 6}
                  oninput={(e) => patchDimensions({ points: Number((e.target as HTMLInputElement).value) })}
                />
              </div>
            {/if}
          {/if}
        {/if}
      </div>
    {/if}
  </div>
{/if}
